import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  listCompanyUsers, inviteTeammate, cancelInvite, setUserRole, removeUser,
  getUser, formatApiErrorDetail,
} from "../lib/api";
import "../studio.css";

export default function Team() {
  const me = getUser();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listCompanyUsers().then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const isOwner = data?.isOwner;
  const domain = data?.company?.domain;

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await inviteTeammate(email.trim());
      toast.success(`Invite sent to ${email.trim()}`);
      setEmail(""); load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Invite failed");
    } finally { setBusy(false); }
  };

  const changeRole = async (u, role) => {
    try { await setUserRole(u.id, role); toast.success(`${u.email} is now ${role}`); load(); }
    catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const kick = async (u) => {
    if (!window.confirm(`Remove ${u.email} from ${data.company.name}?`)) return;
    try { await removeUser(u.id); toast.success(`${u.email} removed`); load(); }
    catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };
  const dropInvite = async (inv) => {
    await cancelInvite(inv.email); toast(`Invite to ${inv.email} cancelled`); load();
  };

  return (
    <div className="app">
        <header className="bar">
          <h1 className="page-title" data-testid="page-title">Team <span className="dim">· {data?.company?.name || "CloudTV"}</span></h1>
          <div className="spacer" />
        </header>

        <div className="admin-wrap">
          <div className="admin-head">
            <h1>Team members</h1>
            <p>Everyone with an <b>@{domain}</b> email can request access. Owners can invite teammates, change roles, and remove people.</p>
          </div>

          {isOwner && (
            <form className="invite-row" onSubmit={invite} data-testid="invite-form">
              <input type="email" placeholder={`teammate@${domain || "company.com"}`} value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />
              <button className="btn primary" type="submit" disabled={busy} data-testid="invite-submit">
                {busy ? "Sending…" : "Send invite"}
              </button>
            </form>
          )}

          <table className="admin-table" data-testid="team-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Sign-in</th><th>Role</th>{isOwner && <th></th>}</tr>
            </thead>
            <tbody>
              {(data?.users || []).map((u) => (
                <tr key={u.id} data-testid={`member-row-${u.id}`}>
                  <td><b>{u.name || "—"}</b>{u.id === me?.id && <span className="tag-you">you</span>}</td>
                  <td className="mono">{u.email}</td>
                  <td className="dim">{u.authProvider === "google" ? "Google" : "Password"}</td>
                  <td>
                    {isOwner && u.id !== me?.id ? (
                      <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`role-${u.id}`}>
                        <option value="owner">owner</option>
                        <option value="member">member</option>
                      </select>
                    ) : (
                      <span className={`pill pill-${u.role === "owner" ? "approved" : "pending"}`}>{u.role}</span>
                    )}
                  </td>
                  {isOwner && (
                    <td className="row-acts">
                      {u.id !== me?.id && <button className="btn danger sm" onClick={() => kick(u)} data-testid={`remove-${u.id}`}>Remove</button>}
                    </td>
                  )}
                </tr>
              ))}
              {(data?.invites || []).map((inv) => (
                <tr key={inv.email} className="invite-row-tr" data-testid={`invite-row-${inv.email}`}>
                  <td className="dim">Invited</td>
                  <td className="mono">{inv.email}</td>
                  <td className="dim">—</td>
                  <td><span className="pill pill-pending">pending invite</span></td>
                  {isOwner && <td className="row-acts"><button className="btn sm" onClick={() => dropInvite(inv)} data-testid={`cancel-invite-${inv.email}`}>Cancel</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
  );
}
